// [목적] TopBar 우측 알림 벨 — 미읽 배지 + 클릭 시 알림 드롭다운 패널
// [이유] 2026-09-20 notification-bell(사용자 요청): 알림을 보려면 사이드바 → /notifications 전체 페이지로
//        이동해야만 했다. 알림은 "지금 보던 화면을 떠나지 않고 훑는" 성격이라 공통 헤더에서 바로 열리는
//        드롭다운이 맞다. TopBar 는 홈/발견/축제/폴리오/프로필/내폴리오/챗/팔로우목록이 공유하므로
//        여기 한 곳에만 달면 전 페이지에 적용된다.
// [사이드 이팩트] 패널을 열 때만 useNotifications 가 활성화된다(enabled: open) — 닫힌 상태에서 전 페이지가
//                 알림 목록을 조회하는 낭비를 막는다. 미읽 카운트(useUnreadCount)는 기존대로 Sidebar 와
//                 동일 훅/캐시를 공유하므로 배지 숫자가 두 곳에서 어긋나지 않는다.
//                 markAllRead / markRead 성공 시 훅 내부가 캐시를 무효화해 Sidebar 배지도 함께 갱신된다.
// [수정 시 고려사항] 패널 내부 목록은 "최근 것 훑기"용이라 무한스크롤을 붙이지 않는다 — 더 보려면
//                   푸터의 "전체 알림 보기"로 /notifications 로 보낸다(두 화면의 역할을 분리).
//                   행 디자인·문구는 components/notifications/notificationShared.tsx 가 단일 소스 —
//                   여기서 별도 마크업을 만들지 말 것.
//                   Popper 는 z-index 가 MUI 기본 modal(1300) 아래로 내려가면 FilterModal 등에 가려지므로
//                   zIndex 를 명시한다.

import { Badge, Box, ButtonBase, ClickAwayListener, Fade, Popper, Typography } from '@mui/material';
import { tokens } from '@gangwoncanvas/ui';
import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import type { NotificationTab, NotificationItem } from '@gangwoncanvas/types';

import { LineIcon } from '../icons/LineIcons.js';
import {
  useNotifications,
  useUnreadCount,
  useMarkNotificationRead,
  useMarkAllRead,
} from '../../hooks/useNotifications.js';

import { NotificationRow } from './notificationShared.js';

const c = tokens.color;
const L = 'light' as const;
const TEXT_HI = c.ink.high[L];
const TEXT_MID = c.ink.mid[L];
const TEXT_LOW = c.ink.low[L];
const BORDER = c.line.base[L];
const PAPER0 = c.paper[0][L];
const PAPER1 = c.paper[1][L];
const PAPER2 = c.paper[2][L];
const BRAND = c.brand[500][L];
const BRAND_TEXT = c.brand[600][L];
const DANGER = c.danger[500][L];

/** [목적] 패널 탭 3종 — NotificationsPage 와 동일 라인업(ADR-T3-2) */
const PANEL_TABS: ReadonlyArray<{ id: NotificationTab; label: string }> = [
  { id: 'all', label: '전체' },
  { id: 'social', label: '소셜' },
  { id: 'system', label: '공지' },
];

/**
 * [목적] 패널에 한 번에 보여줄 최근 알림 수
 * [이유] 드롭다운은 "훑기"용 — 스크롤이 길어지면 전체 페이지와 역할이 겹친다.
 * [수정 시 고려사항] 늘릴 경우 패널 maxHeight(520px) 도 함께 검토할 것.
 */
const PANEL_ITEM_LIMIT = 6;

/**
 * [목적] 알림 벨 버튼 + 드롭다운 패널
 * [이유] 파일 헤더 주석 참조.
 * [사이드 이팩트] open=true 인 동안에만 알림 목록을 조회한다.
 * [수정 시 고려사항] 배지 수는 useUnreadCount() 단일 소스 — 패널이 들고 있는 목록에서 세지 말 것
 *                   (패널은 최근 N건만 갖고 있어 실제 미읽 수와 다르다).
 */
export function NotificationBell(): JSX.Element {
  const navigate = useNavigate();
  const anchorRef = useRef<HTMLButtonElement>(null);
  const [open, setOpen] = useState(false);
  const [tab, setTab] = useState<NotificationTab>('all');

  const unreadCount = useUnreadCount();
  const { data, isPending, isError } = useNotifications(tab, { enabled: open });
  const { mutate: markRead } = useMarkNotificationRead();
  const { mutate: markAllRead, isPending: isMarkingAll } = useMarkAllRead();

  const items: NotificationItem[] = (data?.pages.flatMap((pg) => pg.items) ?? []).slice(
    0,
    PANEL_ITEM_LIMIT,
  );

  const close = (): void => setOpen(false);

  // [목적] ESC 로 패널 닫기 — document 레벨 리스너
  // [이유] 패널 Box 에만 onKeyDown 을 달았더니, 여는 순간 포커스는 여전히 벨 버튼에 있어(Popper 는
  //        포커스를 옮기지 않는다) keydown 이 패널까지 버블링되지 않았다 — ESC 가 무반응(실측 확인).
  //        document 에 붙여야 벨·패널·본문 어디에 포커스가 있든 동일하게 닫힌다.
  // [사이드 이팩트] open=true 인 동안만 등록되고 닫힐 때 해제된다(상시 리스너 없음).
  // [수정 시 고려사항] capture 단계가 아닌 bubble 사용 — 패널 안 입력컨트롤이 ESC 를 먼저 소비해야 하는
  //                   경우(예: 검색 추가)를 위해서다.
  useEffect(() => {
    if (!open) return;
    const onKeyDown = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [open]);

  return (
    <Box sx={{ position: 'relative', flexShrink: 0 }}>
      <ButtonBase
        ref={anchorRef}
        onClick={() => setOpen((v) => !v)}
        aria-label={unreadCount > 0 ? `알림 (미읽 ${unreadCount}개)` : '알림'}
        aria-haspopup="dialog"
        aria-expanded={open}
        sx={{
          width: 36,
          height: 36,
          borderRadius: `${tokens.radius.m}px`,
          color: TEXT_HI,
          border: `1px solid ${open ? BORDER : 'transparent'}`,
          bgcolor: open ? PAPER2 : 'transparent',
          transition: `all ${tokens.motion.fast.duration}ms ${tokens.motion.fast.easing}`,
          '&:hover': { bgcolor: PAPER2, borderColor: BORDER },
          '&:focus-visible': { outline: `2px solid ${BRAND}`, outlineOffset: 2 },
        }}
      >
        {/* [목적] 미읽 수 배지 — 99 초과는 '99+' 로 축약(헤더 폭 보호)
            [이유] 숫자가 길어지면 배지가 벨 아이콘을 덮는다. */}
        <Badge
          badgeContent={unreadCount}
          max={99}
          invisible={unreadCount === 0}
          sx={{
            '& .MuiBadge-badge': {
              bgcolor: DANGER,
              color: PAPER0,
              fontSize: 9.5,
              fontWeight: 800,
              fontFamily: 'JetBrains Mono, ui-monospace, monospace',
              minWidth: 16,
              height: 16,
              border: `2px solid ${PAPER1}`,
              top: 2,
              right: 1,
            },
          }}
        >
          <LineIcon name="notifications" size={19} />
        </Badge>
      </ButtonBase>

      <Popper
        open={open}
        anchorEl={anchorRef.current}
        placement="bottom-end"
        transition
        // MUI modal 기본 z-index(1300) 위에 두어 페이지 모달/오버레이에 가려지지 않게 한다.
        sx={{ zIndex: 1350 }}
        modifiers={[{ name: 'offset', options: { offset: [0, 10] } }]}
      >
        {({ TransitionProps }) => (
          <Fade {...TransitionProps} timeout={150}>
            <Box>
              <ClickAwayListener onClickAway={close}>
                <Box
                  role="dialog"
                  aria-label="알림 목록"
                  sx={{
                    width: 392,
                    maxHeight: 520,
                    display: 'flex',
                    flexDirection: 'column',
                    overflow: 'hidden',
                    bgcolor: PAPER0,
                    border: `1px solid ${BORDER}`,
                    borderRadius: `${tokens.radius.l}px`,
                    boxShadow: '0 20px 48px rgba(20,24,31,.20)',
                  }}
                >
                  {/* 패널 헤더 */}
                  <Box sx={{ p: '15px 16px 0' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: '9px', mb: '12px' }}>
                      <Typography
                        sx={{
                          fontSize: 15.5,
                          fontWeight: 800,
                          color: TEXT_HI,
                          letterSpacing: '-0.3px',
                        }}
                      >
                        알림
                      </Typography>
                      {unreadCount > 0 && (
                        <Box
                          component="span"
                          aria-label={`미읽 알림 ${unreadCount}개`}
                          sx={{
                            bgcolor: BRAND,
                            color: PAPER0,
                            fontSize: 10,
                            fontWeight: 800,
                            px: '7px',
                            py: '2px',
                            borderRadius: 99,
                            fontFamily: 'JetBrains Mono, ui-monospace, monospace',
                          }}
                        >
                          {unreadCount}
                        </Box>
                      )}
                      <Box sx={{ flex: 1 }} />
                      <ButtonBase
                        onClick={() => markAllRead()}
                        disabled={unreadCount === 0 || isMarkingAll}
                        aria-label="모두 읽음"
                        sx={{
                          fontSize: 11.5,
                          fontWeight: 700,
                          color: unreadCount > 0 ? BRAND_TEXT : TEXT_LOW,
                          px: '4px',
                          '&:disabled': { opacity: 0.45 },
                          '&:focus-visible': { outline: `2px solid ${BRAND}`, outlineOffset: 2 },
                        }}
                      >
                        모두 읽음
                      </ButtonBase>
                    </Box>

                    <Box
                      role="tablist"
                      aria-label="알림 탭"
                      sx={{
                        display: 'flex',
                        gap: '6px',
                        pb: '12px',
                        borderBottom: `1px solid ${BORDER}`,
                      }}
                    >
                      {PANEL_TABS.map((t) => {
                        const active = tab === t.id;
                        return (
                          <ButtonBase
                            key={t.id}
                            role="tab"
                            aria-selected={active}
                            aria-label={`탭 ${t.label}`}
                            onClick={() => setTab(t.id)}
                            sx={{
                              px: '13px',
                              height: 30,
                              borderRadius: 99,
                              fontSize: 12,
                              fontWeight: active ? 700 : 600,
                              bgcolor: active ? TEXT_HI : PAPER0,
                              color: active ? PAPER0 : TEXT_MID,
                              border: `1px solid ${active ? TEXT_HI : BORDER}`,
                              transition: `all ${tokens.motion.fast.duration}ms ${tokens.motion.fast.easing}`,
                              '&:hover': active ? {} : { borderColor: BRAND, color: BRAND_TEXT },
                              '&:focus-visible': {
                                outline: `2px solid ${BRAND}`,
                                outlineOffset: 2,
                              },
                            }}
                          >
                            {t.label}
                          </ButtonBase>
                        );
                      })}
                    </Box>
                  </Box>

                  {/* 목록 */}
                  <Box sx={{ flex: 1, overflowY: 'auto' }}>
                    {isPending && (
                      <Typography
                        sx={{ p: '40px 16px', textAlign: 'center', fontSize: 12, color: TEXT_LOW }}
                        role="status"
                      >
                        불러오는 중…
                      </Typography>
                    )}
                    {isError && !isPending && (
                      <Typography
                        sx={{ p: '40px 16px', textAlign: 'center', fontSize: 12, color: TEXT_MID }}
                      >
                        알림을 불러오지 못했어요
                      </Typography>
                    )}
                    {!isPending && !isError && items.length === 0 && (
                      <Box sx={{ p: '38px 20px', textAlign: 'center' }}>
                        <Box
                          aria-hidden="true"
                          sx={{
                            width: 52,
                            height: 52,
                            borderRadius: '50%',
                            mx: 'auto',
                            mb: '12px',
                            background: 'linear-gradient(145deg,#E3EDFB,#EAF3EC)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            color: BRAND,
                          }}
                        >
                          <LineIcon name="notifications" size={24} />
                        </Box>
                        <Typography sx={{ fontSize: 13, fontWeight: 700, color: TEXT_HI }}>
                          새 알림이 없어요
                        </Typography>
                        <Typography sx={{ fontSize: 11.5, color: TEXT_LOW, mt: '5px' }}>
                          좋아요·댓글·팔로우가 생기면 여기에 표시돼요
                        </Typography>
                      </Box>
                    )}
                    {!isPending &&
                      items.map((item) => (
                        <NotificationRow
                          key={item.id}
                          item={item}
                          density="panel"
                          onMarkRead={(id) => markRead(id)}
                          onNavigate={close}
                        />
                      ))}
                  </Box>

                  {/* 푸터 — 전체 보기 */}
                  <Box sx={{ borderTop: `1px solid ${BORDER}`, bgcolor: PAPER1, p: '9px' }}>
                    <ButtonBase
                      onClick={() => {
                        close();
                        navigate('/notifications');
                      }}
                      aria-label="전체 알림 보기"
                      sx={{
                        width: '100%',
                        py: '8px',
                        borderRadius: `${tokens.radius.m}px`,
                        fontSize: 12.5,
                        fontWeight: 700,
                        color: TEXT_HI,
                        '&:hover': { bgcolor: PAPER2 },
                        '&:focus-visible': { outline: `2px solid ${BRAND}`, outlineOffset: -2 },
                      }}
                    >
                      전체 알림 보기 →
                    </ButtonBase>
                  </Box>
                </Box>
              </ClickAwayListener>
            </Box>
          </Fade>
        )}
      </Popper>
    </Box>
  );
}
